/**
 * 全局路由状态管理模块
 * 功能：
 * 1. 管理路由加载状态
 * 2. 控制游戏项目路由权限
 * 3. 维护多级菜单状态
 * 4. 处理路由缓存和刷新逻辑
 */
import { defineStore, storeToRefs } from 'pinia';
import { STORE_KEY } from '@/config/storeKey';
import { computed, ref, shallowReactive, shallowRef } from 'vue';
import { getBaseInfoByGame, type TRspGetBaseInfoByGame, type TActionItem } from '@/service/baseinfo';
import { useRoute, type RouteRecordNormalized } from 'vue-router';
import { useGlobalGameStore } from './game.store';
import { notFoundRoute, refreshRoute } from '@/router';
import { useStorage } from '@vueuse/core';
import { isString, uniqWith, isPlainObject } from 'lodash-es';

// 页面加载状态枚举
enum PageLoadState {
  unload,
  loading,
  error,
}
// 路由加载状态枚举
enum RouterLoadState {
  loading,
  done,
  selectGame,
}
/**
 * 生成游戏基础信息请求参数
 * @param game - 可选游戏代码，默认使用当前全局游戏代码
 */
export const genBaseInfoByGameParams = ({ game = '' } = {}) => {
  const { gameCode } = storeToRefs(useGlobalGameStore());
  return {
    game: game || gameCode.value,
  };
};

export const useRouterStore = defineStore(STORE_KEY.GLOBAL.ROUTER, () => {
  // ----------- 页面状态管理 ------------
  const pageState = ref<PageLoadState>(PageLoadState.unload);
  const setPageLoading = () => {
    pageState.value = PageLoadState.loading;
  };
  const setPageErr = () => {
    pageState.value = PageLoadState.error;
  };
  const setPageLoadEnd = () => {
    pageState.value = PageLoadState.unload;
  };
  const isPageLoading = computed(() => pageState.value === PageLoadState.loading);
  const isPageLoadErr = computed(() => pageState.value === PageLoadState.error);

  // ----------- 路由状态管理 ------------
  const routerState = ref<RouterLoadState>(RouterLoadState.done);
  const setRouterNeedSelectGame = () => {
    routerState.value = RouterLoadState.selectGame;
  };
  const setRouterLoading = () => {
    routerState.value = RouterLoadState.loading;
  };
  const setRouterDone = () => {
    routerState.value = RouterLoadState.done;
  };
  const isRouterNeedSelectGame = computed(() => routerState.value === RouterLoadState.selectGame);
  const isRouterLoading = computed(() => routerState.value === RouterLoadState.loading);
  const isRouterDone = computed(() => routerState.value === RouterLoadState.done);

  // ----------- 游戏路由配置 ------------
  const gameCode = ref('');
  const setRouterGame = (game: string) => gameCode.value !== game && (gameCode.value = game);

  // 首路由管理
  const firstRouteName = ref<string>();
  const firstRoute = computed<RouteRecordNormalized | undefined>(() => {
    const route = allRoutes.value.find((item) => item?.name?.toString() === firstRouteName.value);
    return route;
  });
  const setFirstRoute = (route: RouteRecordNormalized) => {
    firstRouteName.value = route?.name?.toString();
  };
  const gameStore = useGlobalGameStore();
  const { gameCfg } = storeToRefs(gameStore);

  // 允许访问的路由配置（按游戏代码分组）
  const allowRoutes = shallowReactive<{ [game: string]: Array<string> }>({});

  /**
   * 当前游戏允许的路由列表
   * 默认包含'My Project'和'My Project / Real Time'路由
   */
  const nowGameAllowRoutes = computed<Array<string>>(() => allowRoutes[gameCode.value] || []);

  // ----------- 路由记录管理 ------------
  const allRoutesInner = shallowRef<RouteRecordNormalized[]>([]);
  const setAllRoutes = (list: RouteRecordNormalized[]) => {
    allRoutesInner.value = list;
  };
  const allRoutes = computed<RouteRecordNormalized[]>(() => {
    return allRoutesInner.value;
  });

  // 当前路由权限检查
  const route = useRoute();
  const isNowRouterAllow = computed(() => {
    const currentRouteName = route?.name?.toString();
    return (
      notFoundRoute.name === currentRouteName ||
      !!(currentRouteName && nowGameAllowRoutes.value.includes(currentRouteName))
    );
  });

  // 检查游戏路由是否需要加载
  const checkNowGameIsNeedLoad = (gameCode: string) => !allowRoutes[gameCode] || isRouterNeedSelectGame.value;

  // ----------- 操作权限管理 ------------
  const actionList = shallowReactive<{ [game: string]: Record<string, Array<TActionItem>> }>({});
  const nowRouterActionList = computed<Array<TActionItem>>(() => {
    const l1ParentName = route?.meta?.parent?.[0];
    return l1ParentName ? actionList?.[gameCode.value]?.[l1ParentName] || [] : [];
  });

  const getRouterGameCode = (isFirstInit: boolean, params: TRspGetBaseInfoByGame): string | undefined => {
    if (!isPlainObject(params)) {
      return;
    }
    const { game: legalGame, actions, allow_games: allowGames } = params;
    let { allow_routes: allowRoutesFrFetch } = params;

    if (isFirstInit && allowGames) {
      gameStore.setGameCfg(allowGames);
    }

    // 去重
    allowRoutesFrFetch = uniqWith(allowRoutesFrFetch);
    if (legalGame) {
      allowRoutes[legalGame] = allowRoutesFrFetch;
      actionList[legalGame] = actions;
      // setRouterGame(legalGame);
      return legalGame;
    }
    return;
  };
  // ----------- 路由初始化逻辑 ------------
  const initGameRouter = async ({ game = '' }): Promise<string | undefined> => {
    const hasInit = !!allowRoutes[game];
    if (hasInit) {
      return game;
    }

    const isFirstInit = !gameCfg.value;

    const params = await getBaseInfoByGame(
      genBaseInfoByGameParams({ game }),
      undefined,
      (data: TRspGetBaseInfoByGame) => {
        const routerGameCode = getRouterGameCode(isFirstInit, data);
        if (routerGameCode) refreshRoute(routerGameCode);
      },
    );
    return getRouterGameCode(isFirstInit, params);
  };
  // ----------- sidebar and secondLevelSideBar collapsed  ------------
  // ----------- 菜单折叠状态 ------------
  const collapsed = useStorage('menu.collapsed', false, localStorage);
  const setCollapsed = (val: boolean) => {
    collapsed.value = val;
  };
  const showCollapsed = () => {
    collapsed.value = false;
  };
  const hideCollapsed = () => {
    collapsed.value = true;
  };
  const toggleCollapsed = () => {
    collapsed.value = !collapsed.value;
  };
  const isShowSecondMenu = useStorage('menu.isShowSecondMenu', true, localStorage);
  const setIsShowSecondMenu = (val: boolean) => {
    isShowSecondMenu.value = val;
  };

  // ----------- SecondLevelSiderBar  ------------
  const secondRouter = computed(() => {
    if (!firstRoute?.value?.children?.length) {
      return [];
    }
    const currentRoute = route;
    return firstRoute?.value?.children
      ?.map((childRoute) => {
        const isShow = !childRoute?.meta?.hide;
        const isAllow =
          isShow &&
          childRoute?.meta?.icon &&
          isString(childRoute.name) &&
          nowGameAllowRoutes.value.includes(childRoute.name as string);
        if (!isAllow) return false;

        const allowChildren = childRoute?.children?.filter(
          (childRoute) =>
            (isString(childRoute.name) && nowGameAllowRoutes.value.includes(childRoute.name as string)) ||
            childRoute.name === currentRoute?.name,
        );

        if (childRoute?.meta?.dir && (Array.isArray(allowChildren) ? allowChildren : []).length === 0) return false;

        return {
          ...childRoute,
          children: allowChildren,
        };
      })
      ?.filter((v) => v) as Array<RouteRecordNormalized>;
  });

  const secondRouteName = ref<string>(); // 选中的二级路由名
  const threeRouteName = ref<string>(); // 选中的三级路由名
  const setSecondThreeRouteName = (routes: RouteRecordNormalized[]) => {
    if (routes.length >= 2) {
      secondRouteName.value = routes[1]?.name?.toString();
    } else {
      secondRouteName.value = '';
    }
    if (routes.length >= 3) {
      threeRouteName.value = routes[2]?.name?.toString();
    } else {
      threeRouteName.value = '';
    }
  };
  const threeRouter = computed(() => {
    // 选中的三级路路由
    if (secondRouter?.value?.length === 0) return [];
    const currentSecondRouter = secondRouter?.value?.find((item) => item?.name?.toString() === secondRouteName.value);
    const child = currentSecondRouter?.children;
    return (Array.isArray(child) ? child : []).filter((r) => {
      const isShow = !r?.meta?.isHidePc;
      return !r?.meta?.hide && isShow && (r?.meta?.title || r?.meta?.name);
    });
  });

  return {
    route,
    gameCode,
    allRoutes,
    firstRoute,
    nowGameAllowRoutes,
    isNowRouterAllow,
    checkNowGameIsNeedLoad,
    initGameRouter,
    setAllRoutes,

    setRouterGame,
    setRouterLoading,
    setRouterDone,
    setRouterNeedSelectGame,
    setFirstRoute,
    isRouterLoading,
    isRouterDone,
    isRouterNeedSelectGame,

    nowRouterActionList,

    setPageLoading,
    setPageErr,
    setPageLoadEnd,
    isPageLoading,
    isPageLoadErr,

    setCollapsed,
    showCollapsed,
    hideCollapsed,
    toggleCollapsed,
    collapsed,

    setIsShowSecondMenu,
    isShowSecondMenu,

    allowRoutes,
    firstRouteName,
    secondRouter,
    secondRouteName,
    setSecondThreeRouteName,
    threeRouter,
    threeRouteName,
  };
});
